import Axios from "axios";
import { StripeProvider } from "context/stripe-provider";
import HPVTreatmentDisclaimer from "modals/hpv-treatment-disclaimer";
import Appointment, {
    FollowUpType,
    isCompleted,
    isScheduled,
} from "models/appointment";
import { SCREENING_FLOW } from "models/screening-flow";
import React, {
    ReactElement,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { Loader } from "react-feather";
import { NotificationManager } from "react-notifications";
import { Redirect, useHistory } from "react-router-dom";
import { animateScroll as scroll } from "react-scroll";
import {
    trackBookingFlowAppointmentBooked,
    trackBookingFlowClinicSelected,
    trackBookingFlowMedicalEvaluationSubmitted,
    trackBookingFlowPrivacyConsentAccepted,
    trackBookingFlowPrivacyConsentDeclined,
} from "utils/analytics/booking-flow-tracking";
import { UserActionEvent } from "utils/analytics/events";
import { INVALID_APPOINTMENT_DATE_ERROR } from "utils/api-error-types";
import { suppressErrorNotificationForTypes } from "utils/axios-error-notification";
import { removePreSelectedClinicId } from "utils/local-storage";
import {
    getAppointmentHold,
    getBookingFollowUpType,
    getIsEngageAppointment,
    getPreSelectedLabType,
    getScreeningFlow,
    hasScreeningFlow,
    removeAppointmentHold,
    removeBookingFollowUpType,
    removeIsEngageAppointment,
    removeScreeningFlow,
    setPreSelectedLabType,
} from "utils/session-storage";
import {
    BookingFlowEmbeddedShell,
    BookingFlowStepId,
} from "../../../components/booking-flow";
import { Breadcrumbs, Header, Modal } from "../../../components";
import HoldTimerBanner, {
    getRemainingHoldTimeInSeconds,
} from "../../../components/appointment-hold-banner";
import useAnalytics from "../../../hooks/analytics";
import useAuthUser from "../../../hooks/auth-user";
import useBookingFlowRedesign from "../../../hooks/useBookingFlowRedesign";
import useLanguage from "../../../hooks/language";
import { getConsentModalTitle } from "../../../modals/consent/consent-flow";
import {
    AppointmentHoldExpiredModal,
    AppointmentNotAvailableModal,
    ConsentModal,
    ConsentModalView,
    ExpiredQuestionnaireModal,
    MedicalRecommendationModal,
} from "../../../modals";
import SuggestedMedicalTest from "../../../models/suggested-medical-test";
import User, {
    expiredPendingQuestionnaire,
    getAppointmentNeedingMissedPayment,
    hasFollowUpPhoneAppointmentToCompleteForFlow,
    hasPaidLastQuestionnaire,
    upcomingScreeningAppointmentForFlow,
} from "../../../models/user";
import ENDPOINTS from "../../../utils/endpoints";
import routes from "../../../utils/routes";
import DateSelection from "./date-selection";
import HpvRecommendations from "./hpv-flow/recommendations";
import HpvUpfrontPayment from "./hpv-upfront-payments";
import MedicalEvaluation from "./medical-evaluation";
import PersonalInfo from "./personal-info";
import PrivateUpfrontPayment from "./private-upfront-payments";
import PublicUpfrontPayment from "./public-upfront-payments";
import Recommendations from "./recommendations";
import styles from "./screening.module.css";

interface IScreeningPageProperties {
    breadcrumbsClassName?: string;
}

export interface ScreeningPage {
    content: ReactElement;
    title: string;
}

enum SCREENING_POSITION {
    PERSONAL_INFO = 0,
    MEDICAL_EVALUATION = 1,
    PAYMENT = 2, // page is optional
    RECOMMENDATIONS = 3,
    DATE_SELECTION = 4, // page is optional
}

const ScreeningPage: React.FunctionComponent<IScreeningPageProperties> = () => {
    const { push, replace } = useHistory();
    const { translations } = useLanguage();
    const [authUser, setAuthUser] = useAuthUser();
    const tTitles = translations.sectionSelection.makeScreeningSection;
    const tModal = translations.questionnaireSection.modal;
    const tExpired = translations.expiredSection;
    const [analytics] = useAnalytics();
    const isBookingFlowRedesign = useBookingFlowRedesign();

    const screeningFlow = getScreeningFlow();
    const isHpvFlow = screeningFlow === SCREENING_FLOW.HPV;
    const isUserRamqAvailable = !!authUser?.ramqNumber;

    const upcomingAppointment = authUser
        ? upcomingScreeningAppointmentForFlow(authUser, screeningFlow)
        : undefined;

    // Redirect user if he has a follow up phone appointment to complete or need to pay last missed appointment
    if (
        authUser &&
        (hasFollowUpPhoneAppointmentToCompleteForFlow(
            authUser,
            screeningFlow
        ) ||
            !!getAppointmentNeedingMissedPayment(authUser))
    ) {
        return <Redirect to={routes.APPOINTMENTS} />;
    }

    // Refs

    const containerRef = useRef<HTMLDivElement | null>(null);

    // States

    const [followUpType, setFollowUpType] = useState<FollowUpType | undefined>(
        () => getBookingFollowUpType() ?? undefined
    );
    const [isPayingWithCash, setIsPayingWithCash] = useState<boolean>();
    const [consentModalVisible, setConsentModalVisible] = useState(false);
    const [consentModalView, setConsentModalView] = useState<ConsentModalView>(
        "consent"
    );

    // Not to clean but otherwise the modal is shown twice and I don't like it much
    const [
        recommendationModalVisible,
        setRecommendationModalVisible,
    ] = useState(false);
    const [
        expiredQuestionnaireModalVisible,
        setExpiredQuestionnaireModalVisible,
    ] = useState(false);
    // Not to clean but otherwise the modal is shown twice and I don't like it much
    const [
        expiredQuestionnaireModalHasBeenShown,
        setExpiredQuestionnaireModalHasBeenShown,
    ] = useState(false);
    const [
        appointmentNotAvailableModalVisible,
        setAppointmentNotAvailableModalVisible,
    ] = useState(false);
    const [
        appointmentHoldExpiredModalVisible,
        setAppointmentHoldExpiredModalVisible,
    ] = useState(false);
    const [
        shouldShowDateSelectionFallback,
        setShouldShowDateSelectionFallback,
    ] = useState(false);
    const [appointmentDate, setAppointmentDate] = useState("");
    const [currentIndex, setCurrentIndex] = useState(
        SCREENING_POSITION.PERSONAL_INFO
    );
    const [fromUserQuestionnaireId, setFromUserQuestionnaireId] = useState<
        number | undefined
    >();
    const [selectedMedicalTests, setSelectedMedicalTests] = useState<
        SuggestedMedicalTest[] | undefined
    >();

    const [isLoading, setIsLoading] = useState(false);
    const [checkoutInProgress, setCheckoutInProgress] = useState(false);
    const isBookingAppointmentRef = useRef(false);
    const holdExpiryInFlightRef = useRef(false);
    const isLoadingRef = useRef(isLoading);
    const checkoutInProgressRef = useRef(checkoutInProgress);

    useEffect(() => {
        isLoadingRef.current = isLoading;
    }, [isLoading]);

    useEffect(() => {
        checkoutInProgressRef.current = checkoutInProgress;
    }, [checkoutInProgress]);

    const setCheckoutInProgressSynced = useCallback((active: boolean) => {
        checkoutInProgressRef.current = active;
        setCheckoutInProgress(active);
    }, []);

    const suppressInvalidAppointmentDateNotification = suppressErrorNotificationForTypes(
        [INVALID_APPOINTMENT_DATE_ERROR]
    );

    // Effects

    useEffect(() => {
        if (
            authUser &&
            expiredPendingQuestionnaire(authUser) &&
            !expiredQuestionnaireModalHasBeenShown
        ) {
            setExpiredQuestionnaireModalVisible(true);
            setExpiredQuestionnaireModalHasBeenShown(true);
        }
    }, [authUser]);

    useEffect(() => {
        if (expiredQuestionnaireModalVisible)
            setExpiredQuestionnaireModalHasBeenShown(true);
    }, [expiredQuestionnaireModalVisible]);

    useEffect(() => {
        if (!containerRef.current) return;
        if (currentIndex === SCREENING_POSITION.DATE_SELECTION) return;

        scroll.scrollTo(containerRef.current.offsetTop - 100, {
            duration: 500,
        });
    }, [currentIndex]);

    // After the personal info screen we must convert labtype to private
    // if there's no RAMQ and the user chose a public lab type
    useEffect(() => {
        if (
            currentIndex === SCREENING_POSITION.MEDICAL_EVALUATION &&
            !getPreSelectedLabType() &&
            !authUser?.ramqNumber
        ) {
            setPreSelectedLabType(true);
        }
    }, [currentIndex]);

    useLayoutEffect(() => {
        if (!hasScreeningFlow()) {
            replace(routes.HOME);
        }
    }, [replace]);

    // Handlers

    const onConsent = () => {
        setConsentModalVisible(false);
        setRecommendationModalVisible(true);
        setCurrentIndex(SCREENING_POSITION.MEDICAL_EVALUATION);

        if (isBookingFlowRedesign && !upcomingAppointment) {
            trackBookingFlowPrivacyConsentAccepted(analytics);
        } else if (!isBookingFlowRedesign) {
            analytics
                ?.cdp()
                ?.trackEvent(UserActionEvent.ScreeningPrivacyConsentAccepted);
        }
    };

    const onDeclineConsent = () => {
        setConsentModalVisible(false);
        setConsentModalView("consent");
        if (isBookingFlowRedesign && !upcomingAppointment) {
            trackBookingFlowPrivacyConsentDeclined(analytics);
        } else if (!isBookingFlowRedesign) {
            analytics
                ?.cdp()
                ?.trackEvent(UserActionEvent.ScreeningPrivacyConsentDeclined);
        }
        replace(routes.HOME);
        removeScreeningFlow();
    };

    const onConsentModalRequestClose = () => {
        setConsentModalVisible(false);
        setConsentModalView("consent");
    };

    const consentModalTitle = getConsentModalTitle(consentModalView, {
        consentHeader: tModal.consentHeader,
        refusalExitTitle: tModal.refusalExitTitle,
    });

    const onCompleteQuestionnaire = (
        latestUserQuestionnaireData: Required<
            Pick<User, "lastUserQuestionnaire">
        >
    ) => {
        if (isBookingFlowRedesign && !upcomingAppointment) {
            trackBookingFlowMedicalEvaluationSubmitted(
                analytics,
                screeningFlow
            );
        }

        // update authUser without calling /me
        setAuthUser({
            ...(authUser as User),
            ...latestUserQuestionnaireData,
        });
        // next page is recommendations (then optional payment / date)
        setCurrentIndex(currentIndex + 1);
    };

    const onCompletePayment = async (
        hasSavedCardDetails: boolean,
        resolvedFollowUpType: FollowUpType
    ) => {
        const resolvedPayWithCash = !hasSavedCardDetails;

        setCheckoutInProgressSynced(true);
        try {
            // update authUser without calling /me
            setAuthUser({
                ...(authUser as User),
                canMakePayments: hasSavedCardDetails,
            });
            setIsPayingWithCash(resolvedPayWithCash);
            setFollowUpType(resolvedFollowUpType);

            const appointmentHold = getAppointmentHold();
            if (appointmentHold) {
                await bookAppointment(
                    appointmentHold.datetime,
                    appointmentHold.clinicId,
                    null,
                    undefined,
                    resolvedFollowUpType,
                    resolvedPayWithCash
                );
            }
        } finally {
            setCheckoutInProgressSynced(false);
        }
    };

    const onRestartPayment = () => {
        NotificationManager.info(
            translations.paymentSection.chargeErrorNotification
        );
        // update authUser without calling /me
        setAuthUser({ ...(authUser as User), canMakePayments: false });
        setCurrentIndex(SCREENING_POSITION.PAYMENT);
    };

    const onRestartQuestionnaireClick = () => {
        // return to personal info and make it possible to no longer see the payment page
        setFromUserQuestionnaireId(authUser?.lastUserQuestionnaire.id);
        setCurrentIndex(SCREENING_POSITION.PERSONAL_INFO);

        // update authUser without calling /me
        setAuthUser({
            ...(authUser as User),
            lastUserQuestionnaire: undefined,
        });
    };

    const onSubmit = (tests: SuggestedMedicalTest[]) => {
        setSelectedMedicalTests(tests);
        // we are either on the 3rd or 4th page, hence the use of currentIndex
        setCurrentIndex(currentIndex + 1);
    };

    const onAppointmentNotAvailable = (dateTime: string) => {
        setAppointmentDate(dateTime);
        setAppointmentNotAvailableModalVisible(true);
    };

    const onAppointmentNotAvailableModalClose = () => {
        setAppointmentNotAvailableModalVisible(false);

        if (shouldShowDateSelectionFallback) {
            setCurrentIndex(SCREENING_POSITION.DATE_SELECTION);
        }
    };

    const holdExpiredHandledRef = useRef(false);

    const isHoldExpiryPaused = (): boolean => {
        return (
            isLoadingRef.current ||
            checkoutInProgressRef.current ||
            isBookingAppointmentRef.current
        );
    };

    const runAppointmentHoldExpired = useCallback(async () => {
        if (holdExpiredHandledRef.current || holdExpiryInFlightRef.current) {
            return;
        }
        if (isHoldExpiryPaused()) {
            return;
        }

        holdExpiryInFlightRef.current = true;
        try {
            let refreshedUser: User | null = null;
            let meRefreshSucceeded = false;
            try {
                const { data } = await Axios.get<User>(ENDPOINTS.ME);
                refreshedUser = data;
                meRefreshSucceeded = true;
                setAuthUser(data);
            } catch {
                refreshedUser = authUser ?? null;
            }

            if (isHoldExpiryPaused()) {
                return;
            }

            const activeExpiry = refreshedUser?.appointmentHoldExpiry;
            const sessionHold = getAppointmentHold();

            if (
                activeExpiry &&
                getRemainingHoldTimeInSeconds(activeExpiry) > 0
            ) {
                return;
            }

            if (!activeExpiry && !sessionHold) {
                return;
            }

            const holdExpiredOnServer =
                (activeExpiry &&
                    getRemainingHoldTimeInSeconds(activeExpiry) === 0) ||
                (meRefreshSucceeded && !activeExpiry && !!sessionHold);

            if (!holdExpiredOnServer) {
                return;
            }

            holdExpiredHandledRef.current = true;
            removeAppointmentHold();

            setAppointmentHoldExpiredModalVisible(true);
        } finally {
            holdExpiryInFlightRef.current = false;
        }
    }, [authUser, setAuthUser]);

    const onAppointmentHoldExpired = useCallback(() => {
        void runAppointmentHoldExpired();
    }, [runAppointmentHoldExpired]);

    const onAppointmentHoldExpiredRestart = useCallback(() => {
        replace(routes.AVAILABILITY);
    }, [replace]);

    const pauseHoldExpiry = isLoading || checkoutInProgress;

    const handleAppointmentAvailabilityError = (
        error: unknown,
        dateTime: string
    ) => {
        if (
            Axios.isAxiosError(error) &&
            error.response?.data?.type === INVALID_APPOINTMENT_DATE_ERROR
        ) {
            removeAppointmentHold();
            setShouldShowDateSelectionFallback(true);
            onAppointmentNotAvailable(dateTime);
        }
    };

    // Network

    const bookAppointment = async (
        datetime: string,
        clinicId: number,
        prepDate: string | null,
        isPrepRefused?: boolean,
        resolvedFollowUpType?: FollowUpType,
        resolvedPayWithCash?: boolean
    ) => {
        if (!authUser?.lastUserQuestionnaire || !selectedMedicalTests) return;
        if (isBookingAppointmentRef.current) return;

        isBookingAppointmentRef.current = true;
        isLoadingRef.current = true;
        setIsLoading(true);

        const chosenMedicalTests = selectedMedicalTests.map(
            ({ medicalTest }) => ({
                medicalTestId: medicalTest.id,
            })
        );
        const body: Record<string, unknown> = {
            userQuestionnaireId: authUser.lastUserQuestionnaire.id,
            chosenMedicalTests,
            datetime,
            clinicId,
            ...(prepDate && { prepDate }),
            isPrepRefused,
            followUpType: resolvedFollowUpType ?? followUpType,
            payWithCash: resolvedPayWithCash ?? isPayingWithCash,
            isEngageAppointment: getIsEngageAppointment(),
        };

        if (!isUserRamqAvailable) {
            body.usesPrivateLab = !authUser?.ramqNumber;
        } else {
            body.usesPrivateLab = getPreSelectedLabType();
        }

        try {
            const newScreeningAppointment = await Axios.post<Appointment>(
                ENDPOINTS.APPOINTMENTS,
                body,
                suppressInvalidAppointmentDateNotification
            );

            removePreSelectedClinicId();
            removeAppointmentHold();
            removeBookingFollowUpType();
            removeIsEngageAppointment();

            // refresh user and exit screening page
            const { data: userData } = await Axios.get<User>(ENDPOINTS.ME);
            setAuthUser(userData);
            NotificationManager.success(
                translations.notification.createdAppointment
            );

            if (isBookingFlowRedesign) {
                trackBookingFlowAppointmentBooked(analytics, {
                    screeningFlow,
                    appointmentCount:
                        userData.appointments?.filter(
                            (appointment) =>
                                isCompleted(appointment) ||
                                isScheduled(appointment)
                        )?.length ?? 0,
                    lab: body.usesPrivateLab ? "private" : "public",
                    consultation:
                        (body.followUpType as FollowUpType) ??
                        followUpType ??
                        FollowUpType.Regular,
                });
            } else {
                if (isPrepRefused !== undefined && !isPrepRefused) {
                    analytics
                        ?.cdp()
                        ?.trackEvent(
                            UserActionEvent.ScreeningPrepAppointmentBooked
                        );
                }

                const eventProperties = {
                    appointmentCount:
                        (userData as User).appointments?.filter(
                            (appointment) =>
                                isCompleted(appointment) ||
                                isScheduled(appointment)
                        )?.length ?? 0,
                    lab: body.usesPrivateLab ? "private" : "public",
                    consultation: body.followUpType,
                    screeningType: screeningFlow,
                };
                analytics
                    ?.cdp()
                    ?.trackEvent(
                        UserActionEvent.ScreeningAppointmentBooked,
                        eventProperties
                    );
                analytics
                    ?.web()
                    ?.trackEvent(
                        UserActionEvent.ScreeningAppointmentBooked,
                        eventProperties
                    );
            }

            push(
                routes.SCREENING_APPOINTMENT_CONFIRMATION({
                    id: newScreeningAppointment.data.id,
                })
            );
        } catch (error) {
            handleAppointmentAvailabilityError(error, datetime);

            const updatedUserData = error?.response?.data?.user;
            if (updatedUserData && updatedUserData.canMakePayments === false) {
                onRestartPayment();
            }
            isBookingAppointmentRef.current = false;
            isLoadingRef.current = false;
            setIsLoading(false);
        }
    };

    const pages: ScreeningPage[] = [
        {
            title: tTitles.information,
            content: (
                <div
                    className={styles.page}
                    key={SCREENING_POSITION.PERSONAL_INFO}
                >
                    <PersonalInfo
                        onSubmit={() => {
                            setConsentModalView("consent");
                            setConsentModalVisible(true);
                        }}
                    />
                </div>
            ),
        },
        {
            title: tTitles.medicalEvaluation,
            content: (
                <div
                    className={styles.page}
                    key={SCREENING_POSITION.MEDICAL_EVALUATION}
                >
                    <MedicalEvaluation
                        onSubmit={onCompleteQuestionnaire}
                        fromUserQuestionnaireId={fromUserQuestionnaireId}
                    />
                </div>
            ),
        },
    ];

    // Legacy : user has filled a questionnaire and was charged, but didn't book an appointment
    const wasChargedWithoutScheduling =
        authUser &&
        expiredPendingQuestionnaire(authUser) &&
        hasPaidLastQuestionnaire(authUser);

    // Load shorter screening form when user update questionnaire answers after scheduling
    const willSeeDateSelectionPage = !upcomingAppointment;

    const hpvRecommendation =
        authUser?.lastUserQuestionnaire?.hpvRecommendation;

    const renderRecommendationContent = () => {
        if (screeningFlow === SCREENING_FLOW.STI) {
            return (
                <Recommendations
                    upcomingAppointment={upcomingAppointment}
                    selectedMedicalTests={selectedMedicalTests}
                    onRestartQuestionnaireClick={onRestartQuestionnaireClick}
                    onSubmit={onSubmit}
                />
            );
        }

        if (!hpvRecommendation) {
            return (
                <div className={styles["loader-container"]}>
                    <div className={styles["loader"]}>
                        <Loader className="spinorama" />
                        <p>{translations.common.loading}</p>
                    </div>
                </div>
            );
        }

        return (
            <HpvRecommendations
                upcomingAppointment={upcomingAppointment}
                hpvRecommendation={hpvRecommendation}
                onSubmit={onSubmit}
            />
        );
    };

    const recommendationsPage = {
        title: tTitles.recommendations,
        content: (
            <div
                className={styles.page}
                key={SCREENING_POSITION.RECOMMENDATIONS}
            >
                {renderRecommendationContent()}
            </div>
        ),
    };

    const upfrontPaymentsPage = {
        title: tTitles.payment,
        content: (
            <div className={styles.page} key={SCREENING_POSITION.PAYMENT}>
                <StripeProvider>
                    {isHpvFlow ? (
                        <HpvUpfrontPayment
                            onSelectFollowUp={setFollowUpType}
                            onCompletePayment={onCompletePayment}
                        />
                    ) : !isUserRamqAvailable || getPreSelectedLabType() ? (
                        <PrivateUpfrontPayment
                            medicalTests={selectedMedicalTests ?? []}
                            onSelectFollowUp={setFollowUpType}
                            onCompletePayment={onCompletePayment}
                        />
                    ) : (
                        <PublicUpfrontPayment
                            selectedFollowUp={followUpType}
                            onSelectFollowUp={setFollowUpType}
                            onCompletePayment={onCompletePayment}
                        />
                    )}
                </StripeProvider>
            </div>
        ),
    };

    const dateSelectionPage = {
        title: tTitles.date,
        content: (
            <div
                className={`${styles.page} container`}
                key={SCREENING_POSITION.DATE_SELECTION}
            >
                <DateSelection
                    onSubmit={(datetime, clinicId, prepDate, isPrepRefused) => {
                        if (isBookingFlowRedesign) {
                            trackBookingFlowClinicSelected(analytics, clinicId);
                        }
                        bookAppointment(
                            datetime,
                            clinicId,
                            prepDate,
                            isPrepRefused
                        );
                    }}
                    loading={isLoading}
                    selectedFollowUpType={followUpType}
                    trackBookingFlowClinicListFilter={isBookingFlowRedesign}
                />
            </div>
        ),
    };

    pages.push(recommendationsPage);
    let paymentStepIndex: number | null = null;
    if (willSeeDateSelectionPage && !wasChargedWithoutScheduling) {
        paymentStepIndex = pages.length;
        pages.push(upfrontPaymentsPage);
    }
    if (shouldShowDateSelectionFallback) {
        pages.push(dateSelectionPage);
    }

    const useBookingFlowScreeningChrome = isBookingFlowRedesign;
    const dateSelectionStepIndex = shouldShowDateSelectionFallback
        ? pages.length - 1
        : null;
    const isBookingFlowPaymentStep =
        useBookingFlowScreeningChrome &&
        paymentStepIndex !== null &&
        currentIndex === paymentStepIndex;
    const isBookingFlowDateStep =
        useBookingFlowScreeningChrome &&
        dateSelectionStepIndex !== null &&
        currentIndex === dateSelectionStepIndex;
    const isBookingFlowFormStep =
        useBookingFlowScreeningChrome &&
        !isBookingFlowPaymentStep &&
        !isBookingFlowDateStep &&
        (paymentStepIndex === null || currentIndex < paymentStepIndex);

    let bookingFlowStepNavStep: BookingFlowStepId | null = null;
    if (isBookingFlowFormStep) {
        bookingFlowStepNavStep = "form";
    } else if (isBookingFlowPaymentStep) {
        bookingFlowStepNavStep = "pay";
    } else if (isBookingFlowDateStep) {
        bookingFlowStepNavStep = "clinic";
    }

    const pageContent = bookingFlowStepNavStep ? (
        <BookingFlowEmbeddedShell
            currentStep={bookingFlowStepNavStep}
            skipConsultationStep={isHpvFlow ? true : undefined}
            belowHoldTimer={!!authUser?.appointmentHoldExpiry}
        >
            {pages[currentIndex]?.content}
        </BookingFlowEmbeddedShell>
    ) : (
        pages[currentIndex]?.content
    );

    return (
        <div
            ref={containerRef}
            className={`${styles["page-wrapper"]} ${
                useBookingFlowScreeningChrome
                    ? styles.pageWrapperBookingFlow
                    : ""
            }`}
        >
            {/* Modals */}
            <Modal
                title={consentModalTitle}
                visible={consentModalVisible}
                onVisibilityChange={(visible) => {
                    setConsentModalVisible(visible);
                    if (!visible) {
                        setConsentModalView("consent");
                    }
                }}
                onRequestClose={onConsentModalRequestClose}
                className={styles.consentModal}
            >
                <ConsentModal
                    view={consentModalView}
                    onViewChange={setConsentModalView}
                    onConsent={onConsent}
                    onExitScreening={onDeclineConsent}
                />
            </Modal>
            <Modal
                title={isHpvFlow ? undefined : tModal.recommendationHeader}
                showHeader={!isHpvFlow}
                visible={recommendationModalVisible}
                onVisibilityChange={setRecommendationModalVisible}
                className={
                    isHpvFlow ? "hpv-treatment-disclaimer-modal" : undefined
                }
            >
                {isHpvFlow ? (
                    <HPVTreatmentDisclaimer
                        onClose={() => setRecommendationModalVisible(false)}
                    />
                ) : (
                    <MedicalRecommendationModal
                        onClose={() => setRecommendationModalVisible(false)}
                    />
                )}
            </Modal>
            <Modal
                title={tExpired.header}
                visible={expiredQuestionnaireModalVisible}
                onVisibilityChange={setExpiredQuestionnaireModalVisible}
            >
                <ExpiredQuestionnaireModal
                    onClose={() => setExpiredQuestionnaireModalVisible(false)}
                />
            </Modal>
            <AppointmentNotAvailableModal
                appointmentDate={appointmentDate}
                visible={appointmentNotAvailableModalVisible}
                onClose={onAppointmentNotAvailableModalClose}
            />
            <AppointmentHoldExpiredModal
                visible={appointmentHoldExpiredModalVisible}
                onRestart={onAppointmentHoldExpiredRestart}
            />

            <Header />

            {authUser?.appointmentHoldExpiry &&
                !appointmentHoldExpiredModalVisible && (
                    <HoldTimerBanner
                        expiry={authUser.appointmentHoldExpiry}
                        variant={
                            useBookingFlowScreeningChrome
                                ? "bookingFlow"
                                : "default"
                        }
                        onExpired={onAppointmentHoldExpired}
                        pauseExpiry={pauseHoldExpiry}
                    />
                )}

            {/* Page content */}

            {!useBookingFlowScreeningChrome && (
                <Breadcrumbs
                    crumbTitles={pages.map(({ title }) => title)}
                    currentIndex={currentIndex}
                />
            )}

            {pageContent}
        </div>
    );
};

export default ScreeningPage;
